// =====================================================================================
// FILE: src/services/bidirectionalSync.js
//
// Replaces 6 Airtable automations with React-driven sync between:
//   - Source tables (Game Timeline, Team List) <-> Operator tables (CG Operator View, Ribbon Operator View)
//
// DIRECTION 1 - Source -> Operator (replaces "Full Sync Asset Router"):
//   When a MatchFlow row is saved/edited, sync fields + create/delete operator rows
//   based on asset links. Handles fingerprint gating and ingestion-aware status.
//
// DIRECTION 2 - Operator -> Source (replaces 4x "Operator -> Parent Status Sync"):
//   When Lumen8 operators change Content Status or Comments, sync back to parent.
//   Rejected propagates immediately, approval requires ALL siblings approved.
//
// =====================================================================================

import { getOperatorBase } from '../config/operatorBases';

const TOKEN = process.env.REACT_APP_AIRTABLE_TOKEN;
const BASE_ID = process.env.REACT_APP_AIRTABLE_BASE_ID;

// Source tables (MatchFlow Builder) - live in BASE_ID
const GAME_TIMELINE_TABLE_ID = process.env.REACT_APP_AIRTABLE_TABLE_ID;              // "Out of Game" = Game Timeline
const TEAM_LIST_TABLE_ID = process.env.REACT_APP_AIRTABLE_TEAMLIST_TABLE_ID || "";

// Operator tables + Assets table - live in the CLUB-SPECIFIC base (not BASE_ID)
const DEFAULT_CLUB = process.env.REACT_APP_DEFAULT_CLUB || '';
const _opBase = DEFAULT_CLUB ? getOperatorBase(DEFAULT_CLUB) : null;
const OPERATOR_BASE_ID = _opBase?.baseId || BASE_ID;     // Club-specific base for operator tables
const CG_OPERATOR_TABLE_ID = _opBase?.cgTableId || '';
const RIBBON_OPERATOR_TABLE_ID = _opBase?.ribbonTableId || '';
const ASSETS_TABLE_ID = _opBase?.assetsTableId || process.env.REACT_APP_AIRTABLE_ASSETS_TABLE_ID || '';
const FIXTURES_TABLE_ID = _opBase?.fixturesTableId || process.env.REACT_APP_AIRTABLE_FIXTURES_TABLE_ID || '';

// =====================================================================================
// CONFIGURATION - Asset field mapping
//
// Defines which source item fields contribute to CG vs Ribbon operator rows.
// CG = Computer Graphics (Xpression screens: Big Screen, Corner Screen, SuperScreen)
// Ribbon = LED ribbon boards (LED strips, 360 LED, Tiled LED, Interchange)
//
// Each entry: { sourceKey: field name on the App.js item object }
// =====================================================================================

const CG_ASSET_FIELDS = [
  'bigGfxVideosId',
  'cornerGfxVideosId',
  'superFullId',
  'super169Id',
  'superSideId',
];

const RIBBON_ASSET_FIELDS = [
  'ledAllTypesId',
  'mcgInterchangeId',
  'marvelInterchangeId',
  'marvel360Id',
  'mcg360Id',
  'mcgTiledLedId',
  'marvelTiledLedId',
];

// Operator destination field names (linked record fields on operator tables)
const CG_ASSET_FIELD_NAME = 'CG Content';
const RIBBON_ASSET_FIELD_NAME = 'Ribbon Content';

// Source field name stored on operator rows for back-referencing
const SOURCE_RECORD_ID_FIELD = 'Source Record ID';

// Venue -> ingest field name on Assets table
const VENUE_INGEST_FIELD_MAP = {
  MCG: 'MCG Ingested',
  MRV: 'Marvel Ingested',    // React uses 'MRV', automation uses 'MARVEL'
  MARVEL: 'Marvel Ingested',
};

// Fields that sync from source -> operator are handled in buildOperatorPayload()
// Note: 'Lumen8 Comments' is never synced source -> operator (operator-owned field)

// Status values
const STATUS = {
  NEW_SUBMISSION: 'New Submission',
  EXAMPLE_CONTENT: 'Example Content',
  RESUBMITTED: 'Resubmitted',
  READY_FOR_TESTING: 'Ready for Testing',
  READY_FOR_PLAYOUT: 'Ready for Playout',
  REJECTED: 'Rejected',
  APPROVED: 'Approved by Lumen8',
  CONTENT_UNLOCKED: 'Content Unlocked',
};

// Statuses that allow the source -> operator sync to create/update rows
// Note: 'Example Content' is excluded — these are structural scaffold rows in MatchFlow
// that keep categories visible in Airtable views and should not sync to operator.
const SOURCE_GATE_OPEN = [STATUS.NEW_SUBMISSION, STATUS.RESUBMITTED, STATUS.CONTENT_UNLOCKED];

// ── Startup diagnostics ──
console.log('[bidirectionalSync] Configuration:', {
  DEFAULT_CLUB,
  operatorBaseFound: !!_opBase,
  OPERATOR_BASE_ID: OPERATOR_BASE_ID || '(empty!)',
  BASE_ID: BASE_ID || '(empty!)',
  sameBase: OPERATOR_BASE_ID === BASE_ID,
  CG_OPERATOR_TABLE_ID: CG_OPERATOR_TABLE_ID || '(empty!)',
  RIBBON_OPERATOR_TABLE_ID: RIBBON_OPERATOR_TABLE_ID || '(empty!)',
  ASSETS_TABLE_ID: ASSETS_TABLE_ID || '(empty!)',
  FIXTURES_TABLE_ID: FIXTURES_TABLE_ID || '(empty!)',
  SOURCE_RECORD_ID_FIELD,
  hasToken: !!TOKEN,
});

// =====================================================================================
// AIRTABLE HELPERS
//
// tableUrl(baseId, tableId) - builds Airtable API URL for the given base + table
// Source tables use BASE_ID, operator + assets tables use OPERATOR_BASE_ID
// =====================================================================================

function tableUrl(baseId, tableId) {
  return `https://api.airtable.com/v0/${baseId}/${tableId}`;
}

async function airtableFetch(url, options = {}) {
  if (!TOKEN) {
    throw new Error('[bidirectionalSync] Missing REACT_APP_AIRTABLE_TOKEN');
  }

  const res = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${TOKEN}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`[bidirectionalSync] Response not JSON: ${text.slice(0, 200)}`);
  }

  if (!res.ok) {
    const errMsg = data?.error?.message || JSON.stringify(data);
    throw new Error(`[bidirectionalSync] Airtable error (${res.status}): ${errMsg}`);
  }

  return data;
}

// =====================================================================================
// SHARED HELPERS
// =====================================================================================

/**
 * Check if operator tables are configured
 */
export function isBidirectionalSyncConfigured() {
  return Boolean(CG_OPERATOR_TABLE_ID || RIBBON_OPERATOR_TABLE_ID);
}

/**
 * Load all operator rows that reference a given source record ID.
 * Uses OPERATOR_BASE_ID since operator tables live in the club-specific base.
 * Fetches all fields to avoid encoding issues with spaces in field names.
 *
 * @param {string} operatorTableId - CG or Ribbon operator table ID
 * @param {string} sourceRecordId - The parent/source record's Airtable ID
 * @returns {Promise<Array>} - Array of operator records
 */
async function loadOperatorRowsBySourceId(operatorTableId, sourceRecordId) {
  if (!operatorTableId || !sourceRecordId) return [];

  const formula = `{${SOURCE_RECORD_ID_FIELD}}='${sourceRecordId}'`;
  const params = new URLSearchParams();
  params.set('filterByFormula', formula);

  try {
    const url = `${tableUrl(OPERATOR_BASE_ID, operatorTableId)}?${params.toString()}`;
    console.log(`[bidirectionalSync] loadOperatorRows: querying ${operatorTableId} for sourceId=${sourceRecordId}`);
    const data = await airtableFetch(url);
    const rows = data.records || [];
    console.log(`[bidirectionalSync] loadOperatorRows: found ${rows.length} existing row(s)`, 
      rows.map(r => ({ id: r.id, sourceRecId: r.fields?.[SOURCE_RECORD_ID_FIELD], fields: Object.keys(r.fields || {}) }))
    );
    return rows;
  } catch (err) {
    console.error('[bidirectionalSync] loadOperatorRowsBySourceId FAILED:', operatorTableId, err.message || err);
    return [];
  }
}

/**
 * Create a record in an Airtable table
 */
async function createRecord(baseId, tblId, fields) {
  console.log(`[bidirectionalSync] createRecord: table=${tblId}`, {
    sourceRecordId: fields[SOURCE_RECORD_ID_FIELD],
    fieldNames: Object.keys(fields),
  });
  const data = await airtableFetch(tableUrl(baseId, tblId), {
    method: 'POST',
    body: JSON.stringify({ records: [{ fields }] }),
  });
  const created = data.records?.[0] || null;
  // Verify Source Record ID was actually written
  if (created) {
    const writtenSourceId = created.fields?.[SOURCE_RECORD_ID_FIELD];
    if (!writtenSourceId) {
      console.error(`[bidirectionalSync] ⚠️ CRITICAL: "${SOURCE_RECORD_ID_FIELD}" was NOT saved on created record ${created.id}. ` +
        `This field may not exist, be read-only (lookup/formula), or be a linked record field. ` +
        `This WILL cause duplicate rows on next sync!`);
    } else {
      console.log(`[bidirectionalSync] createRecord: ${created.id} created with ${SOURCE_RECORD_ID_FIELD}=${writtenSourceId}`);
    }
  }
  return created;
}

/**
 * Update a record in an Airtable table
 */
async function updateRecord(baseId, tblId, recordId, fields) {
  const data = await airtableFetch(tableUrl(baseId, tblId), {
    method: 'PATCH',
    body: JSON.stringify({ records: [{ id: recordId, fields }] }),
  });
  return data.records?.[0] || null;
}

/**
 * Delete records from an Airtable table (max 10 per request)
 */
async function deleteRecords(baseId, tblId, recordIds) {
  if (!recordIds.length) return;

  for (let i = 0; i < recordIds.length; i += 10) {
    const chunk = recordIds.slice(i, i + 10);
    const params = chunk.map(id => `records[]=${id}`).join('&');
    await airtableFetch(`${tableUrl(baseId, tblId)}?${params}`, { method: 'DELETE' });
  }
}

/**
 * Fetch a single record by ID from a table (returns null if not found).
 * Always fetches the full record to avoid field name encoding issues with Airtable API.
 */
async function fetchRecord(baseId, tblId, recordId) {
  if (!tblId || !recordId) return null;

  try {
    const data = await airtableFetch(`${tableUrl(baseId, tblId)}/${recordId}`);
    return data || null;
  } catch (err) {
    // 404 = record not in this table (expected for Direction 2 fallback)
    if (err.message?.includes('404') || err.message?.includes('NOT_FOUND')) return null;
    throw err;
  }
}

// =====================================================================================
// DIRECTION 1 - SOURCE -> OPERATOR
//
// Replaces the "VenueFlow - Full Sync Asset Router" Airtable automation.
// Called from App.js after MatchFlow row save/update.
//
// Flow:
//   1. Compute asset fingerprint and compare to last known fingerprint
//   2. If assets changed -> reset Content Status to "New Submission" on source
//   3. Collect CG and Ribbon asset IDs from source item
//   4. Load existing operator rows by Source Record ID
//   5. Create new rows for new assets, delete rows for removed assets
//   6. Update fields on all existing operator rows
// =====================================================================================

/**
 * Compute a fingerprint string from the item's asset link fields.
 * Replaces the Airtable formula field "Asset Fingerprint".
 *
 * @param {object} item - The App.js item object with asset ID fields
 * @returns {string} - Pipe-delimited fingerprint of all asset IDs
 */
export function computeAssetFingerprint(item) {
  if (!item) return '';

  // All asset fields that contribute to the fingerprint
  // (must match the Airtable formula field "Asset Fingerprint" for backward compat)
  const assetFields = [
    'bigGfxVideosId',
    'cornerGfxVideosId',
    'superFullId',
    'super169Id',
    'superSideId',
    'ledAllTypesId',
    // Note: venue-specific fields (interchange, 360, tiled) can be added if they
    // were included in the original fingerprint formula. The current Airtable formula
    // only includes Big Screen, Corner Screen, SuperScreen, and LED fields.
  ];

  return assetFields
    .map(key => item[key] || '')
    .filter(Boolean)
    .join('|');
}

/**
 * Collect asset IDs split by CG vs Ribbon target
 *
 * @param {object} item - The App.js item object
 * @returns {{ cgAssetIds: string[], ribbonAssetIds: string[] }}
 */
function collectAssetIdsBySyncTarget(item) {
  const cgAssetIds = CG_ASSET_FIELDS
    .map(key => item[key])
    .filter(id => id && typeof id === 'string' && id.startsWith('rec'));

  const ribbonAssetIds = RIBBON_ASSET_FIELDS
    .map(key => item[key])
    .filter(id => id && typeof id === 'string' && id.startsWith('rec'));

  return { cgAssetIds, ribbonAssetIds };
}

/**
 * Build the base field payload for operator rows (shared fields from source)
 *
 * @param {object} item - The App.js item object
 * @param {string} sourceRecordId - The source record's Airtable ID
 * @returns {object} - Field payload for Airtable
 */
function buildOperatorPayload(item, sourceRecordId, fixtureRecordId) {
  const payload = {};

  // Map App.js item keys to Airtable field names
  // ⚠️ IMPORTANT: Single Select fields (Category, Destination Venue) must NEVER be
  // set to "" — Airtable rejects empty strings with 422 "Insufficient permissions
  // to create new select option". Use null to clear, or omit the field entirely.
  if (item.name !== undefined) payload['Name'] = item.name || '';
  if (item.clientSponsorName !== undefined) payload['Client or Sponsor Name'] = item.clientSponsorName || '';
  if (item.category) payload['Category'] = item.category;                      // Single Select — only set if truthy
  if (item.clubInstructions !== undefined) payload['Club Instructions'] = item.clubInstructions || '';
  
  // ✅ Destination Venue for Team List records (MCG, MRV, etc.) — Single Select
  if (item.destinationVenue) {
    payload['Destination Venue'] = item.destinationVenue;
    console.log('[bidirectionalSync] buildOperatorPayload - adding Destination Venue:', item.destinationVenue);
  } else {
    console.log('[bidirectionalSync] buildOperatorPayload - skipping empty Destination Venue');
  }

  // Always set source record ID for back-referencing
  payload[SOURCE_RECORD_ID_FIELD] = sourceRecordId;

  // Set Fixtures linked record if available
  if (fixtureRecordId) {
    payload['Fixtures'] = [fixtureRecordId];
  }

  console.log('[bidirectionalSync] buildOperatorPayload result:', payload);
  return payload;
}

/**
 * Check an asset's ingestion status to determine the right operator status.
 * Assets table lives in OPERATOR_BASE_ID (club-specific base).
 *
 * @param {string} assetId - Asset record ID
 * @param {string} venue - Venue key ('MCG', 'MRV', or 'MARVEL')
 * @returns {Promise<string>} - "Ready for Playout" or "Ready for Testing"
 */
async function getOperatorStatusForAsset(assetId, venue) {
  if (!assetId || !ASSETS_TABLE_ID) return STATUS.READY_FOR_TESTING;

  const ingestField = VENUE_INGEST_FIELD_MAP[venue] || VENUE_INGEST_FIELD_MAP.MCG;

  try {
    // Fetch full record (no field filter) to avoid encoding issues with spaces in field names
    const asset = await fetchRecord(OPERATOR_BASE_ID, ASSETS_TABLE_ID, assetId);
    const isIngested = asset?.fields?.[ingestField];
    return isIngested ? STATUS.READY_FOR_PLAYOUT : STATUS.READY_FOR_TESTING;
  } catch (err) {
    console.warn('[bidirectionalSync] Failed to check asset ingestion:', assetId, err);
    return STATUS.READY_FOR_TESTING;
  }
}

/**
 * Resolve a fixture key (e.g. "R1") to a record ID in the OPERATOR base's Fixtures table.
 * This is needed because the operator tables' "Fixtures" linked record field points to
 * the operator base's Fixtures table, not the main base's.
 *
 * @param {string} key - Fixture key (e.g. "R1", "SF1")
 * @returns {Promise<string|null>} - Fixture record ID in the operator base, or null
 */
async function resolveOperatorFixtureId(key) {
  const safe = String(key || '').trim();
  if (!safe || !FIXTURES_TABLE_ID) return null;

  const safeEsc = safe.replace(/"/g, '\\"');
  const fieldCandidates = ['Match ID', 'Fixture Key', 'Fixture', 'Key', 'ID', 'Fixture ID', 'Name'];

  for (const field of fieldCandidates) {
    try {
      const params = new URLSearchParams({
        pageSize: '1',
        filterByFormula: `{${field}}="${safeEsc}"`,
      });
      const data = await airtableFetch(`${tableUrl(OPERATOR_BASE_ID, FIXTURES_TABLE_ID)}?${params.toString()}`);
      if (data.records?.[0]?.id) return data.records[0].id;
    } catch {
      continue;
    }
  }

  return null;
}

// =====================================================================================
// SYNC DEBOUNCE + MUTEX
//
// Prevents duplicate operator rows caused by rapid successive saves for the same record.
// Each asset pick/field change triggers onInlineSave → syncSourceToOperatorViews.
// Without debounce, overlapping calls both see zero rows and both create new ones.
//
// Strategy:
//   - Debounce: Wait 1.5s after last call before executing (resets on each new call)
//   - Mutex: If a sync is already running for a record, queue the latest args and
//     re-run after the current one finishes (ensures final state is always synced)
// =====================================================================================

const _syncTimers = new Map();    // recordId → setTimeout handle
const _syncActive = new Map();    // recordId → true if currently running
const _syncQueued = new Map();    // recordId → { item, options } for the next queued run

const SYNC_DEBOUNCE_MS = 1500;

/**
 * DIRECTION 1: Sync a source record to CG and Ribbon operator views.
 *
 * Call this after any MatchFlow row save/update in App.js.
 * Debounced + mutex-protected to prevent duplicate rows.
 *
 * @param {string} recordId - Source record Airtable ID
 * @param {object} item - The App.js item object (with all fields)
 * @param {object} options
 * @param {string} options.venue - 'MCG' | 'MRV' | 'ALL' (for ingestion status lookup)
 * @param {string} options.sourceTableId - Table ID (Game Timeline or Team List)
 * @param {string} [options.lastFingerprint] - Previously stored fingerprint
 * @returns {Promise<object>} - Sync result summary
 */
export function syncSourceToOperatorViews(recordId, item, options = {}) {
  return new Promise((resolve) => {
    // Clear any pending debounce timer for this record
    if (_syncTimers.has(recordId)) {
      clearTimeout(_syncTimers.get(recordId));
    }

    // If a sync is already running for this record, queue this as the next run
    if (_syncActive.get(recordId)) {
      console.log(`[bidirectionalSync] Sync already active for ${recordId} — queuing latest state`);
      _syncQueued.set(recordId, { item, options });
      resolve({ queued: true });
      return;
    }

    // Debounce: wait SYNC_DEBOUNCE_MS before executing
    const timer = setTimeout(async () => {
      _syncTimers.delete(recordId);
      const result = await _runSyncWithMutex(recordId, item, options);
      resolve(result);
    }, SYNC_DEBOUNCE_MS);

    _syncTimers.set(recordId, timer);
  });
}

async function _runSyncWithMutex(recordId, item, options) {
  _syncActive.set(recordId, true);
  _syncQueued.delete(recordId);

  let result;
  try {
    result = await _executeSyncToOperatorViews(recordId, item, options);
  } catch (err) {
    console.error('[bidirectionalSync] Sync failed:', err);
    result = { error: err.message };
  } finally {
    _syncActive.delete(recordId);
  }

  // If a newer call was queued while we were running, run it now with latest data
  if (_syncQueued.has(recordId)) {
    const queued = _syncQueued.get(recordId);
    _syncQueued.delete(recordId);
    console.log(`[bidirectionalSync] Running queued sync for ${recordId}`);
    return _runSyncWithMutex(recordId, queued.item, queued.options);
  }

  return result;
}

/**
 * DIRECTION 1 IMPLEMENTATION (internal - called via debounced wrapper above)
 */
async function _executeSyncToOperatorViews(recordId, item, options = {}) {
  const { venue = 'MCG', sourceTableId, lastFingerprint = '', preserveOperatorFields = false } = options;

  if (!isBidirectionalSyncConfigured()) {
    console.log('[bidirectionalSync] No operator tables configured - skipping sync');
    return { skipped: true };
  }

  if (!recordId || !item) {
    console.warn('[bidirectionalSync] Missing recordId or item - skipping');
    return { skipped: true };
  }

  // Skip Example Content scaffold rows — these keep categories visible
  // in MatchFlow Builder and should never sync to operator views.
  if (item.contentStatus === STATUS.EXAMPLE_CONTENT) {
    console.log(`[bidirectionalSync] Skipping Example Content scaffold row: ${item.name || recordId}`);
    return { skipped: true, reason: 'example_content' };
  }

  console.log(`[bidirectionalSync] Direction 1: syncing source ${recordId} to operator views`);

  // ──────────────────────────────────────────────
  // 1. Fingerprint gate - detect asset changes
  //    SKIP during force sync (preserveOperatorFields) — we don't want to reset
  //    source statuses or operator fields, just ensure rows exist.
  // ──────────────────────────────────────────────
  const currentFingerprint = computeAssetFingerprint(item);
  let effectiveStatus = item.contentStatus || STATUS.NEW_SUBMISSION;
  let assetsChanged = false;

  if (!preserveOperatorFields) {
    assetsChanged = !!(currentFingerprint && currentFingerprint !== lastFingerprint);

    if (assetsChanged && sourceTableId) {
      console.log('[bidirectionalSync] Asset set changed -> resetting Content Status to New Submission');
      try {
        await updateRecord(BASE_ID, sourceTableId, recordId, {
          'Content Status': STATUS.NEW_SUBMISSION,
          'Last Asset Fingerprint': currentFingerprint,
        });
        effectiveStatus = STATUS.NEW_SUBMISSION;
      } catch (err) {
        console.error('[bidirectionalSync] Failed to reset status on fingerprint change:', err);
      }
    } else if (currentFingerprint && sourceTableId) {
      try {
        await updateRecord(BASE_ID, sourceTableId, recordId, {
          'Last Asset Fingerprint': currentFingerprint,
        });
      } catch {
        // Non-critical
      }
    }
  }

  // ──────────────────────────────────────────────
  // 2. Collect CG and Ribbon asset IDs from item
  // ──────────────────────────────────────────────
  const { cgAssetIds, ribbonAssetIds } = collectAssetIdsBySyncTarget(item);

  // ──────────────────────────────────────────────
  // 2b. Resolve Fixtures linked record ID
  // ──────────────────────────────────────────────
  let fixtureRecordId = null;
  if (item.fixture) {
    // item.fixture may be a record ID (starts with "rec") or a fixture key (e.g. "R1")
    if (typeof item.fixture === 'string' && item.fixture.startsWith('rec')) {
      fixtureRecordId = item.fixture;
    } else {
      try {
        fixtureRecordId = await resolveOperatorFixtureId(item.fixture);
        console.log(`[bidirectionalSync] Fixture "${item.fixture}" resolved to ${fixtureRecordId || 'null (not found)'}`);
      } catch (err) {
        console.warn('[bidirectionalSync] Failed to resolve fixture key:', item.fixture, err);
      }
    }
  }

  // ──────────────────────────────────────────────
  // 3. Sync to CG Operator View
  // ──────────────────────────────────────────────
  const cgResult = await syncToOperatorTable({
    operatorTableId: CG_OPERATOR_TABLE_ID,
    assetFieldName: CG_ASSET_FIELD_NAME,
    assetIds: cgAssetIds,
    sourceRecordId: recordId,
    item,
    effectiveStatus,
    venue,
    fixtureRecordId,
    preserveOperatorFields,
  });

  // ──────────────────────────────────────────────
  // 4. Sync to Ribbon Operator View
  //    Commercial LED is already blocked by shouldSyncToOperator (isCommLed=true).
  //    All Game Timeline + Team List LED rows sync to ribbon regardless of CG status.
  // ──────────────────────────────────────────────
  const ribbonResult = await syncToOperatorTable({
    operatorTableId: RIBBON_OPERATOR_TABLE_ID,
    assetFieldName: RIBBON_ASSET_FIELD_NAME,
    assetIds: ribbonAssetIds,
    sourceRecordId: recordId,
    item,
    effectiveStatus,
    venue,
    fixtureRecordId,
    preserveOperatorFields,
  });

  const result = {
    sourceRecordId: recordId,
    fingerprint: currentFingerprint,
    assetsChanged,
    cg: cgResult,
    ribbon: ribbonResult,
  };

  console.log('[bidirectionalSync] Direction 1 complete:', result);
  return result;
}

/**
 * Sync asset IDs to a single operator table (CG or Ribbon).
 * Creates rows for new assets, deletes rows for removed assets,
 * and updates shared fields on all existing rows.
 * Operator tables live in OPERATOR_BASE_ID.
 */
async function syncToOperatorTable({
  operatorTableId,
  assetFieldName,
  assetIds,
  sourceRecordId,
  item,
  effectiveStatus,
  venue,
  fixtureRecordId,
  preserveOperatorFields = false,
}) {
  if (!operatorTableId) {
    return { skipped: true, reason: 'table not configured' };
  }

  const result = { created: 0, deleted: 0, updated: 0 };

  try {
    // Load existing operator rows for this source record
    const existingRows = await loadOperatorRowsBySourceId(
      operatorTableId,
      sourceRecordId
    );

    // Build a map: assetId -> operator record ID
    // Also track "orphan" rows — rows that exist (by Source Record ID) but have no asset link.
    // These were created by an earlier bug where the linked record format was wrong,
    // so the asset field was silently dropped during creation.
    const existingAssetMap = new Map();
    const orphanRows = [];

    for (const row of existingRows) {
      const links = row.fields?.[assetFieldName];
      let hasAssetLink = false;
      if (Array.isArray(links) && links.length > 0) {
        for (const link of links) {
          const assetId = typeof link === 'string' ? link : link?.id;
          if (assetId) {
            existingAssetMap.set(assetId, row.id);
            hasAssetLink = true;
          }
        }
      }
      if (!hasAssetLink) {
        orphanRows.push(row);
      }
    }

    if (orphanRows.length > 0) {
      console.log(`[bidirectionalSync] Found ${orphanRows.length} orphan row(s) with no ${assetFieldName} link`);
    }

    // -- Unbundle multi-asset operator rows --
    // A previous bug in forceResyncAll bundled multiple assets into a single operator row.
    // Operators need one row per asset (each has its own ingest pipeline).
    // Detect bundled rows, keep the first asset on the existing row, and create
    // new individual rows for the remaining assets.
    for (const row of existingRows) {
      const links = row.fields?.[assetFieldName];
      if (!Array.isArray(links) || links.length <= 1) continue;

      // This row has multiple assets bundled — unbundle it
      const allAssetIds = links.map(l => typeof l === 'string' ? l : l?.id).filter(Boolean);
      if (allAssetIds.length <= 1) continue;

      console.log(`[bidirectionalSync] Unbundling ${allAssetIds.length} assets from row ${row.id} ("${item.name}")`);

      // Keep the first asset on the existing row
      const keepAssetId = allAssetIds[0];
      try {
        await updateRecord(OPERATOR_BASE_ID, operatorTableId, row.id, {
          [assetFieldName]: [keepAssetId],
        });
        console.log(`[bidirectionalSync] Kept asset ${keepAssetId} on existing row ${row.id}`);
      } catch (err) {
        console.error(`[bidirectionalSync] Failed to unbundle row ${row.id}:`, err);
        continue;
      }

      // Create new individual rows for the remaining assets
      const unbundlePayload = buildOperatorPayload(item, sourceRecordId, fixtureRecordId);
      for (let i = 1; i < allAssetIds.length; i++) {
        const assetId = allAssetIds[i];
        try {
          const createPayload = { ...unbundlePayload, [assetFieldName]: [assetId] };
          // Determine status for the new row
          if (effectiveStatus === STATUS.APPROVED) {
            createPayload['Content Status'] = STATUS.APPROVED;
          } else if (SOURCE_GATE_OPEN.includes(effectiveStatus)) {
            const assetStatus = await getOperatorStatusForAsset(assetId, venue);
            if (assetStatus) createPayload['Content Status'] = assetStatus;
          }
          await createRecord(OPERATOR_BASE_ID, operatorTableId, createPayload);
          console.log(`[bidirectionalSync] Unbundled: created new row for asset ${assetId} ("${item.name}")`);
          result.created++;
          await new Promise(r => setTimeout(r, 300));
        } catch (err) {
          console.error(`[bidirectionalSync] Failed to create unbundled row for asset ${assetId}:`, err);
        }
      }

      // Update the existingAssetMap to reflect the unbundled state
      // Remove all but the first asset from the map (the others now have their own rows)
      for (let i = 1; i < allAssetIds.length; i++) {
        existingAssetMap.delete(allAssetIds[i]);
        // Re-add with a new row ID? We don't know it yet, but we can mark it as existing
        // by adding a placeholder so the create loop below doesn't duplicate it.
        existingAssetMap.set(allAssetIds[i], '__unbundled__');
      }
    }

    const sourceAssetSet = new Set(assetIds);
    const basePayload = buildOperatorPayload(item, sourceRecordId, fixtureRecordId);
    const reusedOrphanIds = new Set();

    console.log(`[bidirectionalSync] syncToOperatorTable(${assetFieldName}):`, {
      sourceRecordId,
      sourceAssetIds: [...sourceAssetSet],
      existingRowCount: existingRows.length,
      existingAssetMap: Object.fromEntries(existingAssetMap),
      orphanCount: orphanRows.length,
    });

    // -- Handle asset changes: update existing rows or create new ones --
    // Asset swap detection: if source has exactly 1 asset and existing rows have
    // a DIFFERENT asset (or assets), this is an asset swap — update rows, don't create duplicates.
    // Widened from the original 1:1 check to handle cases where previous syncs
    // created multiple operator rows for the same source record.
    if (sourceAssetSet.size === 1 && existingRows.length >= 1) {
      const newAssetId = [...sourceAssetSet][0];
      if (!existingAssetMap.has(newAssetId)) {
        // Asset swap: update the first existing row with the new asset, delete extras
        let newStatus;
        if (effectiveStatus === STATUS.APPROVED) {
          newStatus = STATUS.APPROVED;
        } else if (SOURCE_GATE_OPEN.includes(effectiveStatus)) {
          newStatus = await getOperatorStatusForAsset(newAssetId, venue);
        }
        const keepRow = existingRows[0];
        const updatePayload = { ...basePayload, [assetFieldName]: [newAssetId] };
        if (newStatus) updatePayload['Content Status'] = newStatus;
        await updateRecord(OPERATOR_BASE_ID, operatorTableId, keepRow.id, updatePayload);
        console.log(`[bidirectionalSync] Asset swap: updated row ${keepRow.id} with new asset ${newAssetId}`);
        result.updated++;

        // Delete any extra rows (duplicates from previous sync bugs)
        if (existingRows.length > 1) {
          const extraRows = existingRows.slice(1);
          const extraIds = extraRows.map(r => r.id);
          try {
            await deleteRecords(OPERATOR_BASE_ID, operatorTableId, extraIds);
            result.deleted += extraIds.length;
            console.log(`[bidirectionalSync] Asset swap: deleted ${extraIds.length} extra row(s)`);
          } catch (delErr) {
            console.warn(`[bidirectionalSync] Asset swap: failed to delete extra rows:`, delErr);
          }
        }

        // Skip normal create/delete flow
        return result;
      }

      // Same asset, no swap needed — fall through to general update path
    }

    // -- Create rows for NEW assets (reuse orphan rows first to avoid duplicates) --
    for (const assetId of sourceAssetSet) {
      if (!existingAssetMap.has(assetId)) {
        // Determine status
        let newStatus;
        if (effectiveStatus === STATUS.APPROVED) {
          newStatus = STATUS.APPROVED;
        } else if (SOURCE_GATE_OPEN.includes(effectiveStatus)) {
          newStatus = await getOperatorStatusForAsset(assetId, venue);
        }

        if (orphanRows.length > 0) {
          // Reuse an orphan row — patch it with the asset link + shared fields
          const orphan = orphanRows.shift();
          const updatePayload = { ...basePayload, [assetFieldName]: [assetId] };
          if (newStatus) updatePayload['Content Status'] = newStatus;
          await updateRecord(OPERATOR_BASE_ID, operatorTableId, orphan.id, updatePayload);
          reusedOrphanIds.add(orphan.id);
          console.log(`[bidirectionalSync] Reused orphan row ${orphan.id} for asset ${assetId}`);
          result.updated++;
        } else {
          // No orphans left — create a fresh row
          const createPayload = { ...basePayload, [assetFieldName]: [assetId] };
          if (newStatus) createPayload['Content Status'] = newStatus;
          await createRecord(OPERATOR_BASE_ID, operatorTableId, createPayload);
          result.created++;
        }
      }
    }

    // -- Delete rows for REMOVED assets --
    const toDelete = [];
    for (const [assetId, operatorRecordId] of existingAssetMap.entries()) {
      if (!sourceAssetSet.has(assetId)) {
        toDelete.push(operatorRecordId);
      }
    }

    // Also delete any remaining orphan rows that weren't reused
    for (const orphan of orphanRows) {
      toDelete.push(orphan.id);
      console.log(`[bidirectionalSync] Deleting unused orphan row ${orphan.id}`);
    }

    if (toDelete.length) {
      await deleteRecords(OPERATOR_BASE_ID, operatorTableId, toDelete);
      result.deleted = toDelete.length;
    }

    // -- Update shared fields on ALL remaining rows (excluding deleted + reused orphans) --
    const rowsToUpdate = existingRows.filter(r =>
      !toDelete.includes(r.id) && !reusedOrphanIds.has(r.id)
    );
    for (const row of rowsToUpdate) {
      const updatePayload = { ...basePayload };

      // Check if the asset on this operator row matches a source asset.
      // If not, the source asset changed and we need to update the link + reset status.
      const links = row.fields?.[assetFieldName];
      const currentAssetId = Array.isArray(links) ? (links[0]?.id || links[0]) : null;
      const assetStale = currentAssetId && !sourceAssetSet.has(currentAssetId);

      if (assetStale && sourceAssetSet.size > 0) {
        // Asset changed but wasn't caught by swap detection (multi-asset source).
        // Don't update the asset link here — the create/delete flow above handles it.
        // But DO reset the status since the current asset link is stale.
        console.log(`[bidirectionalSync] Stale asset on row ${row.id}: current=${currentAssetId}, source=${[...sourceAssetSet].join(',')}`);
      }

      if (!assetStale) {
        // Asset link is current — don't overwrite it
        delete updatePayload[assetFieldName];
      }

      // PRESERVE OPERATOR FIELDS: When force-syncing, don't overwrite operator-owned fields
      // (Content Status, Send To Watch Folder, Built at Venue) on existing rows.
      // These are managed by the operator workflow, not the source sync.
      if (preserveOperatorFields) {
        delete updatePayload['Content Status'];
        // Name/Category/Instructions still update from source (shared fields)
        console.log(`[bidirectionalSync] Preserving operator fields for existing row ${row.id} ("${item.name}")`);
      } else {
        // Normal sync: re-evaluate status from source
        // Status: if source is terminal approved, propagate to operator rows
        if (effectiveStatus === STATUS.APPROVED) {
          updatePayload['Content Status'] = STATUS.APPROVED;
        } else if (SOURCE_GATE_OPEN.includes(effectiveStatus)) {
          // Re-evaluate ingestion status for the asset on this row
          // Use the CURRENT asset (not stale) for status check
          const checkAssetId = assetStale ? null : currentAssetId;
          if (checkAssetId) {
            const assetStatus = await getOperatorStatusForAsset(checkAssetId, venue);
            const currentDestStatus = row.fields?.['Content Status'] || '';
            // Only change status if the computed value differs
            if (assetStatus !== currentDestStatus) {
              updatePayload['Content Status'] = assetStatus;
            }
          } else if (assetStale) {
            // Asset is stale — reset to Ready for Testing until new asset is ingested
            updatePayload['Content Status'] = STATUS.READY_FOR_TESTING;
          }
        }
      }

      await updateRecord(OPERATOR_BASE_ID, operatorTableId, row.id, updatePayload);
      result.updated++;
    }
  } catch (err) {
    console.error(`[bidirectionalSync] Failed to sync to ${assetFieldName}:`, err);
    result.error = err.message;
  }

  return result;
}


// =====================================================================================
// DIRECTION 2 - OPERATOR -> SOURCE
//
// Replaces 4 "VenueFlow - Operator -> Parent Status Sync" automations:
//   CG Operator View -> Game Timeline
//   CG Operator View -> Team List
//   Ribbon Operator View -> Game Timeline
//   Ribbon Operator View -> Team List
//
// Called from OperatorContext after status or comment changes.
//
// Flow:
//   1. Read Source Record ID from the operator record
//   2. Load ALL sibling operator rows from both CG and Ribbon tables
//   3. Merge Lumen8 Comments from all siblings
//   4. Evaluate status logic:
//      - Rejected -> propagates immediately
//      - Content Unlocked -> propagates immediately
//      - Approved by Lumen8 -> only if ALL siblings are approved
//   5. Update the parent (source) record
// =====================================================================================

/**
 * DIRECTION 2: Sync operator status/comments back to the source record.
 *
 * Call this after an operator changes Content Status or Lumen8 Comments
 * on a CG or Ribbon Operator View row.
 *
 * PER-SOURCE SERIALIZATION:
 * Direction 2 is serialized per sourceRecordId via a FIFO mutex. When two
 * siblings of the same source record are approved in quick succession (e.g.
 * CG + Ribbon toggled back-to-back), both fire Direction 2 and both load
 * siblings from Airtable. Without serialization, the first call can read
 * siblings BEFORE the second sibling's status write has been committed, see
 * "not all approved", and either do nothing or overwrite the correct value
 * back to an earlier state. Serializing by sourceRecordId ensures each
 * Direction 2 call finishes its Airtable read+write before the next starts,
 * so by the time the LAST call runs it sees every sibling's committed status.
 *
 * Unlike Direction 1 (which debounces + coalesces to the latest state),
 * Direction 2 calls are NOT coalesced — each status transition (Rejected,
 * Content Unlocked, Approved) is semantically distinct and must be processed
 * in order.
 *
 * @param {string} sourceRecordId - Source Record ID field value from the operator row
 * @param {string} newStatus - The new Content Status value set by the operator
 * @param {object} [options]
 * @param {string} [options.sourceTableId] - If known, the specific source table to update
 * @returns {Promise<object>} - Sync result
 */
const _dir2Chains = new Map(); // sourceRecordId → Promise (tail of the FIFO chain)

export function syncOperatorStatusToSource(sourceRecordId, newStatus, options = {}) {
  if (!sourceRecordId) {
    console.warn('[bidirectionalSync] Missing sourceRecordId - skipping reverse sync');
    return Promise.resolve({ skipped: true });
  }

  const previous = _dir2Chains.get(sourceRecordId) || Promise.resolve();

  const next = previous
    .catch(() => {}) // don't let prior failure block subsequent calls
    .then(() => {
      console.log(`[bidirectionalSync] Direction 2 serialization: starting call for ${sourceRecordId} (status="${newStatus}")`);
      return _executeSyncOperatorStatusToSource(sourceRecordId, newStatus, options);
    });

  // Save the new tail so the NEXT call waits on this one
  _dir2Chains.set(sourceRecordId, next);

  // Clean up the chain entry once it completes AND is still the tail
  // (if another call has already been chained, that one will handle cleanup)
  next.finally(() => {
    if (_dir2Chains.get(sourceRecordId) === next) {
      _dir2Chains.delete(sourceRecordId);
    }
  });

  return next;
}

async function _executeSyncOperatorStatusToSource(sourceRecordId, newStatus, options = {}) {
  const { sourceTableId, clubId, contentType } = options;

  // Get club-specific operator tables if clubId provided, otherwise use defaults
  let cgTableId = CG_OPERATOR_TABLE_ID;
  let ribbonTableId = RIBBON_OPERATOR_TABLE_ID;
  let operatorBaseId = OPERATOR_BASE_ID;
  
  if (clubId) {
    const clubBase = getOperatorBase(clubId);
    if (clubBase) {
      cgTableId = clubBase.cgTableId || '';
      ribbonTableId = clubBase.ribbonTableId || '';
      operatorBaseId = clubBase.baseId || OPERATOR_BASE_ID;
    }
  }

  // Shared tables (LED, TVC, IPTV) - live in Match Builder base (BASE_ID)
  const ledTableId = process.env.REACT_APP_AIRTABLE_LEDENTITLEMENTS_TABLE_ID || '';
  const tvcTableId = process.env.REACT_APP_AIRTABLE_INGAME_TABLE_ID || '';
  const iptvTableId = process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID || '';

  const hasClubTables = cgTableId || ribbonTableId;
  const hasSharedTables = ledTableId || tvcTableId || iptvTableId;

  if (!hasClubTables && !hasSharedTables) {
    console.log('[bidirectionalSync] No operator tables configured - skipping reverse sync');
    return { skipped: true };
  }

  if (!sourceRecordId) {
    console.warn('[bidirectionalSync] Missing sourceRecordId - skipping reverse sync');
    return { skipped: true };
  }

  console.log(`[bidirectionalSync] Direction 2: syncing operator status "${newStatus}" back to source ${sourceRecordId}`);
  console.log('[bidirectionalSync] Direction 2 config:', {
    clubId: clubId || '(default)',
    contentType: contentType || '(unknown)',
    cgTableId: cgTableId || '(empty)',
    ribbonTableId: ribbonTableId || '(empty)',
    ledTableId: ledTableId || '(empty)',
    tvcTableId: tvcTableId || '(empty)',
    iptvTableId: iptvTableId || '(empty)',
    operatorBaseId: operatorBaseId || '(empty)',
    BASE_ID: BASE_ID || '(empty)',
  });

  try {
    // ──────────────────────────────────────────────
    // 1. Load ALL sibling operator rows from ALL tables
    // ──────────────────────────────────────────────
    const loadPromises = [];
    
    // Club-specific tables (CG, Ribbon) - in operator base
    if (cgTableId) {
      loadPromises.push(loadOperatorRowsBySourceIdInBase(operatorBaseId, cgTableId, sourceRecordId).then(rows => ({ type: 'CG', rows })));
    }
    if (ribbonTableId) {
      loadPromises.push(loadOperatorRowsBySourceIdInBase(operatorBaseId, ribbonTableId, sourceRecordId).then(rows => ({ type: 'Ribbon', rows })));
    }
    
    // Shared tables (LED, TVC, IPTV) - in Match Builder base (BASE_ID)
    if (ledTableId) {
      loadPromises.push(loadOperatorRowsBySourceIdInBase(BASE_ID, ledTableId, sourceRecordId).then(rows => ({ type: 'LED', rows })));
    }
    if (tvcTableId) {
      loadPromises.push(loadOperatorRowsBySourceIdInBase(BASE_ID, tvcTableId, sourceRecordId).then(rows => ({ type: 'TVC', rows })));
    }
    if (iptvTableId) {
      loadPromises.push(loadOperatorRowsBySourceIdInBase(BASE_ID, iptvTableId, sourceRecordId).then(rows => ({ type: 'IPTV', rows })));
    }

    const results = await Promise.all(loadPromises);
    
    // Collect all rows and log counts
    const allRows = [];
    const rowCounts = {};
    for (const { type, rows } of results) {
      rowCounts[type] = rows.length;
      allRows.push(...rows);
    }

    console.log(`[bidirectionalSync] Found sibling rows:`, rowCounts, `Total: ${allRows.length}`);

    if (allRows.length === 0) {
      console.warn('[bidirectionalSync] No operator rows found for source:', sourceRecordId);
      return { skipped: true, reason: 'no operator rows' };
    }

    // Log all sibling statuses for debugging
    console.log('[bidirectionalSync] Sibling row statuses:', allRows.map(r => ({
      id: r.id,
      status: r.fields?.['Content Status'] || '(empty)',
      name: r.fields?.['Name'] || '(no name)',
    })));

    // ──────────────────────────────────────────────
    // 2. Merge Lumen8 Comments from all siblings
    // ──────────────────────────────────────────────
    const mergedComments = allRows
      .map(r => {
        const id = r.fields?.['Lumen8 ID'] || r.id;
        const comment = r.fields?.['Lumen8 Comments'] || '';
        if (!comment) return null;
        return `${id}: ${comment}`;
      })
      .filter(Boolean)
      .join('\n');

    // ──────────────────────────────────────────────
    // 3. Evaluate status logic
    // ──────────────────────────────────────────────
    const payload = {};

    // Always sync comments back to source — including empty string (cleared comments)
    payload['Lumen8 Comments'] = mergedComments || '';

    const allApproved = allRows.every(
      r => (r.fields?.['Content Status'] || '') === STATUS.APPROVED
    );
    const isSingleSibling = allRows.length === 1;
    const soleSiblingApproved = isSingleSibling &&
      (allRows[0].fields?.['Content Status'] || '') === STATUS.APPROVED;

    console.log('[bidirectionalSync] Direction 2 status check:', {
      newStatus,
      siblingCount: allRows.length,
      isSingleSibling,
      soleSiblingApproved,
      allApproved,
      STATUS_APPROVED: STATUS.APPROVED,
      STATUS_REJECTED: STATUS.REJECTED,
    });

    // Rejected -> propagates immediately to parent
    if (newStatus === STATUS.REJECTED) {
      payload['Content Status'] = STATUS.REJECTED;
      console.log('[bidirectionalSync] Setting REJECTED status on source');
    }
    // Content Unlocked -> propagates immediately
    else if (newStatus === STATUS.CONTENT_UNLOCKED) {
      payload['Content Status'] = STATUS.CONTENT_UNLOCKED;
      console.log('[bidirectionalSync] Setting CONTENT_UNLOCKED status on source');
    }
    // Approved by Lumen8 on a single-sibling record -> propagate immediately.
    // A single-sibling record has no other surfaces to wait on, so the multi-sibling
    // allApproved gate is unnecessary here and was observed failing to fire in practice.
    else if (newStatus === STATUS.APPROVED && isSingleSibling && soleSiblingApproved) {
      payload['Content Status'] = STATUS.APPROVED;
      console.log('[bidirectionalSync] Setting APPROVED status on source (single-sibling record, sole sibling approved)');
    }
    // Approved by Lumen8 on a multi-sibling record -> only when ALL siblings are approved.
    // Unchanged behaviour: surface-pair (CG + Ribbon + LED) records still wait for all
    // surfaces to complete before the source record is flipped green.
    else if (newStatus === STATUS.APPROVED && !isSingleSibling && allApproved) {
      payload['Content Status'] = STATUS.APPROVED;
      console.log('[bidirectionalSync] Setting APPROVED status on source (all siblings approved)');
    } else if (newStatus === STATUS.APPROVED && !isSingleSibling && !allApproved) {
      console.log('[bidirectionalSync] NOT setting APPROVED - not all siblings approved yet');
    }

    // ──────────────────────────────────────────────
    // 4. Update the parent (source) record
    //    Source tables live in BASE_ID (not operator base)
    // ──────────────────────────────────────────────
    if (!payload['Content Status'] && payload['Lumen8 Comments'] === undefined) {
      console.log('[bidirectionalSync] No status change and no comments to sync');
      return { skipped: true, reason: 'no changes' };
    }

    // Determine which source table to update
    const resolvedTableId = await resolveSourceTableId(sourceRecordId, sourceTableId);
    if (!resolvedTableId) {
      console.error('[bidirectionalSync] Could not resolve source table for record:', sourceRecordId);
      return { error: 'Source table not found' };
    }

    console.log('[bidirectionalSync] Updating source record:', {
      baseId: BASE_ID,
      tableId: resolvedTableId,
      recordId: sourceRecordId,
      payload,
    });

    await updateRecord(BASE_ID, resolvedTableId, sourceRecordId, payload);

    const result = {
      sourceRecordId,
      sourceTableId: resolvedTableId,
      statusSet: payload['Content Status'] || null,
      commentsSynced: Boolean(mergedComments),
      allApproved,
      siblingCount: allRows.length,
    };

    console.log('[bidirectionalSync] Direction 2 complete:', result);
    return result;
  } catch (err) {
    console.error('[bidirectionalSync] Direction 2 failed:', err);
    return { error: err.message };
  }
}

// Helper to load operator rows from a specific base/table
async function loadOperatorRowsBySourceIdInBase(baseId, tableId, sourceRecordId) {
  if (!tableId || !sourceRecordId) return [];

  const formula = `{${SOURCE_RECORD_ID_FIELD}}='${sourceRecordId}'`;
  const params = new URLSearchParams();
  params.set('filterByFormula', formula);

  try {
    const url = `${tableUrl(baseId, tableId)}?${params.toString()}`;
    console.log(`[bidirectionalSync] loadOperatorRowsInBase: querying ${tableId} in base ${baseId} for sourceId=${sourceRecordId}`);
    const data = await airtableFetch(url);
    const rows = data.records || [];
    console.log(`[bidirectionalSync] loadOperatorRowsInBase: found ${rows.length} existing row(s)`);
    return rows;
  } catch (err) {
    const msg = err.message || '';
    // Tables like LED, TVC, IPTV may not have Source Record ID — skip silently
    if (msg.includes('Unknown field names') || msg.includes('422')) {
      return [];
    }
    console.error('[bidirectionalSync] loadOperatorRowsBySourceIdInBase FAILED:', tableId, msg);
    return [];
  }
}

/**
 * Resolve which source table a record belongs to.
 * Tries the provided tableId first, then falls back to checking both tables.
 * Source tables live in BASE_ID (the main MatchFlow base).
 */
async function resolveSourceTableId(recordId, hintTableId) {
  // If we already know the table, verify it exists there
  if (hintTableId) {
    const rec = await fetchRecord(BASE_ID, hintTableId, recordId);
    if (rec) return hintTableId;
  }

  // Try Game Timeline first (most common)
  if (GAME_TIMELINE_TABLE_ID) {
    const rec = await fetchRecord(BASE_ID, GAME_TIMELINE_TABLE_ID, recordId);
    if (rec) return GAME_TIMELINE_TABLE_ID;
  }

  // Try Team List
  if (TEAM_LIST_TABLE_ID) {
    const rec = await fetchRecord(BASE_ID, TEAM_LIST_TABLE_ID, recordId);
    if (rec) return TEAM_LIST_TABLE_ID;
  }

  return null;
}

// =====================================================================================
// CONVENIENCE EXPORTS
// =====================================================================================

/**
 * Determine the source table ID based on mode flags.
 * Call from App.js to pass into syncSourceToOperatorViews.
 */
export function getSourceTableIdForMode({ isTeam }) {
  if (isTeam) return TEAM_LIST_TABLE_ID;
  // Default = Game Timeline (Out of Game)
  // Note: In Game, IPTV, LED modes don't sync to operator views
  // because those table types have different workflows
  return GAME_TIMELINE_TABLE_ID;
}

/**
 * Check if a mode should trigger Direction 1 sync.
 * Only Game Timeline and Team List modes sync to operator views.
 */
export function shouldSyncToOperator({ isTeam, isInGame, isLed, isIptv, isTvc, isCommLed }) {
  // Only Game Timeline (out) and Team List sync to CG/Ribbon operator views.
  // TVC and Commercial LED have their own separate operator views/playlists.
  if (isInGame || isLed || isIptv || isTvc || isCommLed) return false;
  return true;
}


// =====================================================================================
// FORCE RESYNC — Reads ALL source records and pushes to operator tables
//
// Use when operator view is missing rows that exist in MatchFlow Builder.
// Reads directly from source Airtable tables (Game Timeline + Team List),
// maps each record to the item format, and fires Direction 1 for each.
// =====================================================================================

/**
 * Fetch all records from a table with pagination, optionally filtered by Airtable view
 */
async function fetchAllRecords(baseId, tableId, viewName = null) {
  const all = [];
  let offset = null;
  
  do {
    let url = `https://api.airtable.com/v0/${baseId}/${tableId}?pageSize=100${offset ? `&offset=${offset}` : ''}`;
    if (viewName) url += `&view=${encodeURIComponent(viewName)}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${TOKEN}`, 'Content-Type': 'application/json' },
    });
    if (!res.ok) {
      // If view doesn't exist for this table (e.g., Team List may not have the same view), skip gracefully
      if (res.status === 422 || res.status === 404) {
        console.warn(`[ForceResync] View "${viewName}" not found in table ${tableId} — skipping`);
        return all;
      }
      throw new Error(`Airtable fetch failed: ${res.status}`);
    }
    const data = await res.json();
    all.push(...(data.records || []));
    offset = data.offset || null;
  } while (offset);
  
  return all;
}

/**
 * Map a raw Airtable record to the App.js item format that syncSourceToOperatorViews expects
 */
function mapRawRecordToItem(record) {
  const f = record.fields || {};
  const linked = (val) => Array.isArray(val) ? (val[0] || null) : null;
  
  return {
    id: record.id,
    name: f['Name'] || '',
    category: f['Category'] || '',
    clientSponsorName: f['Client or Sponsor Name'] || '',
    clubInstructions: f['Club Instructions'] || '',
    contentStatus: f['Content Status'] || '',
    destinationVenue: f['Destination Venue'] || '',
    fixture: linked(f['Fixtures']),
    
    // Has Content formula — "Yes" means club has assigned actual content
    // Used by checkSyncStatus to skip placeholder rows with no real assets
    hasContent: f['Has Content'] || '',
    
    // CG asset fields
    bigGfxVideosId: linked(f['Big Screen - GFX & Videos']),
    cornerGfxVideosId: linked(f['Corner Screen - GFX & Videos']),
    superFullId: linked(f['SuperScreen – Fullscreen / Takeovers']),
    super169Id: linked(f['SuperScreen - 20:9 Safe Graphics']),
    superSideId: linked(f['SuperScreen - Side Panels']),
    
    // Ribbon/LED asset fields
    ledAllTypesId: linked(f['LED - All Types']),
    mcgInterchangeId: linked(f['MCG Interchange']),
    marvelInterchangeId: linked(f['Marvel Interchange']),
    marvel360Id: linked(f['Marvel 360 LED']),
    mcg360Id: linked(f['MCG 360 LED']),
    mcgTiledLedId: linked(f['MCG Tiled LED']),
    marvelTiledLedId: linked(f['Marvel Tiled LED']),
    
    // IPTV asset field
    iptvGfxId: linked(f['IPTV GFX']),
  };
}

/**
 * Force resync source records to operator views.
 * SMART APPROACH: Bulk-loads all existing operator rows first, identifies which
 * source records are missing, and only creates those. Much faster and avoids rate limits.
 * 
 * @param {function} onProgress - Called with { current, total, name } for UI updates
 * @param {string} [viewName] - Optional Airtable view name to filter by (e.g., "OR_STK_v_COL_MCG")
 * @returns {Promise<object>} - Summary { total, synced, skipped, errors, created }
 */
export async function forceResyncAllToOperator(onProgress = () => {}, viewName = null) {
  if (!isBidirectionalSyncConfigured()) {
    return { error: 'No operator tables configured' };
  }
  
  const summary = { total: 0, synced: 0, skipped: 0, errors: 0, created: 0, details: [] };
  
  try {
    // 1. Fetch source records (optionally filtered by view/match)
    const IPTV_TABLE_ID = process.env.REACT_APP_AIRTABLE_IPTV_TABLE_ID || '';
    const tables = [];
    if (GAME_TIMELINE_TABLE_ID) tables.push({ id: GAME_TIMELINE_TABLE_ID, label: 'Game Timeline', needsSync: true });
    if (TEAM_LIST_TABLE_ID) tables.push({ id: TEAM_LIST_TABLE_ID, label: 'Team List', needsSync: true });
    if (IPTV_TABLE_ID) tables.push({ id: IPTV_TABLE_ID, label: 'IPTV', needsSync: false });
    
    let allRecords = [];
    for (const table of tables) {
      // Team List is a season-long roster — never filter by fixture view
      const useView = table.id === TEAM_LIST_TABLE_ID ? null : viewName;
      console.log(`[ForceResync] Fetching records from ${table.label}${useView ? ` (view: ${useView})` : ' (all records — season roster)'}...`);
      const records = await fetchAllRecords(BASE_ID, table.id, useView);
      allRecords.push(...records.map(r => ({ ...r, _sourceTableId: table.id, _sourceLabel: table.label, _needsSync: table.needsSync })));
      console.log(`[ForceResync] Found ${records.length} records in ${table.label}`);
    }
    
    summary.total = allRecords.length;
    console.log(`[ForceResync] Total source records: ${summary.total}`);
    
    // 2. Bulk-load ALL existing operator rows from CG and Ribbon tables
    //    This is ONE request per table instead of one per source record.
    onProgress({ current: 0, total: summary.total, name: 'Loading existing operator rows...' });
    
    const existingCgRows = CG_OPERATOR_TABLE_ID 
      ? await fetchAllRecords(OPERATOR_BASE_ID, CG_OPERATOR_TABLE_ID) 
      : [];
    const existingRibbonRows = RIBBON_OPERATOR_TABLE_ID 
      ? await fetchAllRecords(OPERATOR_BASE_ID, RIBBON_OPERATOR_TABLE_ID) 
      : [];
    
    // Build maps of existing operator rows by asset ID (not just source record ID).
    // This ensures multi-asset rows (e.g. cornerscreen + superscreen on same source row)
    // are tracked per-asset, so missing assets are detected and created individually.
    const cgExistingAssetIds = new Set();
    const cgSourceIds = new Set();
    for (const row of existingCgRows) {
      const srcId = row.fields?.[SOURCE_RECORD_ID_FIELD];
      if (srcId) cgSourceIds.add(srcId);
      const links = row.fields?.[CG_ASSET_FIELD_NAME];
      if (Array.isArray(links)) {
        for (const link of links) {
          const assetId = typeof link === 'string' ? link : link?.id;
          if (assetId) cgExistingAssetIds.add(assetId);
        }
      }
    }
    const ribbonExistingAssetIds = new Set();
    const ribbonSourceIds = new Set();
    for (const row of existingRibbonRows) {
      const srcId = row.fields?.[SOURCE_RECORD_ID_FIELD];
      if (srcId) ribbonSourceIds.add(srcId);
      const links = row.fields?.[RIBBON_ASSET_FIELD_NAME];
      if (Array.isArray(links)) {
        for (const link of links) {
          const assetId = typeof link === 'string' ? link : link?.id;
          if (assetId) ribbonExistingAssetIds.add(assetId);
        }
      }
    }
    
    console.log(`[ForceResync] Existing operator rows — CG: ${existingCgRows.length} (${cgSourceIds.size} sources, ${cgExistingAssetIds.size} assets), Ribbon: ${existingRibbonRows.length} (${ribbonSourceIds.size} sources, ${ribbonExistingAssetIds.size} assets)`);
    
    // 3. Identify which source records need syncing.
    //    Check per ASSET ID, not just per source record — a source row with
    //    cornerscreen + superscreen needs TWO operator rows. If only the
    //    cornerscreen row exists, the superscreen asset is still missing.
    const recordsToSync = [];
    for (const record of allRecords) {
      if (!record._needsSync) { summary.skipped++; continue; }
      
      const item = mapRawRecordToItem(record);

      // Skip Example Content scaffold rows — these keep categories visible
      // in MatchFlow Builder and should never sync to operator views.
      if (item.contentStatus === STATUS.EXAMPLE_CONTENT) {
        summary.skipped++;
        continue;
      }

      // Skip items where Airtable's Has Content formula says No — these are placeholder
      // rows with phantom linked records (recIDs exist but no actual file/content)
      if (item.hasContent && !item.hasContent.toLowerCase().startsWith('yes')) {
        summary.skipped++;
        continue;
      }

      const { cgAssetIds, ribbonAssetIds } = collectAssetIdsBySyncTarget(item);
      
      if (cgAssetIds.length === 0 && ribbonAssetIds.length === 0) {
        summary.skipped++;
        continue;
      }

      // Per-asset check: find individual assets that don't have operator rows yet
      const missingCgAssetIds = cgAssetIds.filter(id => !cgExistingAssetIds.has(id));
      const missingRibbonAssetIds = ribbonAssetIds.filter(id => !ribbonExistingAssetIds.has(id));

      const needsCg = missingCgAssetIds.length > 0;
      const needsRibbon = missingRibbonAssetIds.length > 0;
      
      if (needsCg || needsRibbon) {
        recordsToSync.push({ record, item, needsCg, needsRibbon, cgAssetIds: missingCgAssetIds, ribbonAssetIds: missingRibbonAssetIds });
      } else {
        summary.synced++; // All assets already have operator rows
      }
    }
    
    console.log(`[ForceResync] Records needing sync: ${recordsToSync.length} of ${summary.total}`);
    
    // 4. Create missing operator rows (with generous rate limiting)
    //    ONE operator row PER ASSET — matches Direction 1 (real-time sync) behaviour.
    //    Each CG/Ribbon asset gets its own row so operators can see and manage them
    //    individually (e.g. cornerscreen and superscreen have separate ingest pipelines).
    for (let i = 0; i < recordsToSync.length; i++) {
      const { record, item, needsCg, needsRibbon, cgAssetIds, ribbonAssetIds } = recordsToSync[i];
      onProgress({ current: summary.synced + summary.created + i + 1, total: summary.total, name: item.name || '(unnamed)' });
      
      let fixtureRecordId = null;
      if (item.fixture && typeof item.fixture === 'string' && item.fixture.startsWith('rec')) {
        fixtureRecordId = item.fixture;
      } else if (item.fixture) {
        try { fixtureRecordId = await resolveOperatorFixtureId(item.fixture); } catch (e) {}
      }
      
      const basePayload = buildOperatorPayload(item, record.id, fixtureRecordId);
      
      try {
        if (needsCg && CG_OPERATOR_TABLE_ID) {
          // One row PER CG asset (matches Direction 1 behaviour)
          for (const assetId of cgAssetIds) {
            const createPayload = { ...basePayload, [CG_ASSET_FIELD_NAME]: [assetId] };
            await createRecord(OPERATOR_BASE_ID, CG_OPERATOR_TABLE_ID, createPayload);
            summary.created++;
            console.log(`[ForceResync] Created CG row for "${item.name}" (${record.id}) — asset ${assetId}`);
            await new Promise(r => setTimeout(r, 300));
          }
        }
        
        if (needsRibbon && RIBBON_OPERATOR_TABLE_ID) {
          // One row PER Ribbon asset (matches Direction 1 behaviour)
          for (const assetId of ribbonAssetIds) {
            const createPayload = { ...basePayload, [RIBBON_ASSET_FIELD_NAME]: [assetId] };
            await createRecord(OPERATOR_BASE_ID, RIBBON_OPERATOR_TABLE_ID, createPayload);
            summary.created++;
            console.log(`[ForceResync] Created Ribbon row for "${item.name}" (${record.id}) — asset ${assetId}`);
            await new Promise(r => setTimeout(r, 300));
          }
        }
        
        summary.synced++;
      } catch (err) {
        console.error(`[ForceResync] Failed for "${item.name}" (${record.id}):`, err);
        summary.errors++;
        summary.details.push(`Failed: ${item.name} — ${err.message}`);
        // Rate limit recovery — wait longer after errors (likely 429)
        await new Promise(r => setTimeout(r, 2000));
      }
    }
    
    console.log('[ForceResync] Complete:', summary);
    return summary;
    
  } catch (err) {
    console.error('[ForceResync] Fatal error:', err);
    return { ...summary, error: err.message };
  }
}


/**
 * Lightweight sync status check — counts source (MatchFlow) ASSETS vs operator records
 * to detect missing content without performing a full sync.
 * Checks per-asset (not per-source-record) to catch bundled rows or missing multi-asset gaps.
 * Returns { sourceAssetCount, sourceRecordCount, operatorCount, missing, missingItems, synced, status }
 */
export async function checkSyncStatus(viewName = null) {
  if (!isBidirectionalSyncConfigured()) {
    return { sourceAssetCount: 0, sourceRecordCount: 0, sourceCount: 0, operatorCount: 0, missing: 0, synced: true, status: 'not_configured' };
  }

  try {
    // Fetch source records (Game Timeline + Team List)
    const tables = [];
    if (GAME_TIMELINE_TABLE_ID) tables.push({ id: GAME_TIMELINE_TABLE_ID, label: 'Game Timeline' });
    if (TEAM_LIST_TABLE_ID) tables.push({ id: TEAM_LIST_TABLE_ID, label: 'Team List' });

    let sourceRecords = [];
    for (const table of tables) {
      const useView = table.id === TEAM_LIST_TABLE_ID ? null : viewName;
      const records = await fetchAllRecords(BASE_ID, table.id, useView);
      sourceRecords.push(...records);
    }

    // Count source records and individual assets that have content
    // Skip "Example Content" rows and rows where Has Content is not "Yes"
    let sourceRecordCount = 0;
    let sourceAssetCount = 0;
    const sourceAssetDetails = []; // { recordId, assetId, name, category, target: 'CG'|'Ribbon' }
    for (const record of sourceRecords) {
      const item = mapRawRecordToItem(record);
      if (item.contentStatus === STATUS.EXAMPLE_CONTENT) continue;
      if (item.hasContent && !item.hasContent.toLowerCase().startsWith('yes')) continue;
      const { cgAssetIds, ribbonAssetIds } = collectAssetIdsBySyncTarget(item);
      if (cgAssetIds.length > 0 || ribbonAssetIds.length > 0) {
        sourceRecordCount++;
        for (const assetId of cgAssetIds) {
          sourceAssetCount++;
          sourceAssetDetails.push({ recordId: record.id, assetId, name: item.name, category: item.category, target: 'CG' });
        }
        for (const assetId of ribbonAssetIds) {
          sourceAssetCount++;
          sourceAssetDetails.push({ recordId: record.id, assetId, name: item.name, category: item.category, target: 'Ribbon' });
        }
      }
    }

    // Fetch operator records and build per-asset sets
    const existingCgRows = CG_OPERATOR_TABLE_ID
      ? await fetchAllRecords(OPERATOR_BASE_ID, CG_OPERATOR_TABLE_ID)
      : [];
    const existingRibbonRows = RIBBON_OPERATOR_TABLE_ID
      ? await fetchAllRecords(OPERATOR_BASE_ID, RIBBON_OPERATOR_TABLE_ID)
      : [];

    // Build sets of asset IDs that exist in operator tables
    const cgExistingAssetIds = new Set();
    for (const row of existingCgRows) {
      const links = row.fields?.[CG_ASSET_FIELD_NAME];
      if (Array.isArray(links)) {
        for (const link of links) {
          const assetId = typeof link === 'string' ? link : link?.id;
          if (assetId) cgExistingAssetIds.add(assetId);
        }
      }
    }
    const ribbonExistingAssetIds = new Set();
    for (const row of existingRibbonRows) {
      const links = row.fields?.[RIBBON_ASSET_FIELD_NAME];
      if (Array.isArray(links)) {
        for (const link of links) {
          const assetId = typeof link === 'string' ? link : link?.id;
          if (assetId) ribbonExistingAssetIds.add(assetId);
        }
      }
    }

    // Count missing: individual source assets that don't have operator rows
    let missing = 0;
    const missingItems = [];
    for (const detail of sourceAssetDetails) {
      const existingSet = detail.target === 'CG' ? cgExistingAssetIds : ribbonExistingAssetIds;
      if (!existingSet.has(detail.assetId)) {
        missing++;
        missingItems.push({ name: detail.name || '(unnamed)', category: detail.category || '', where: detail.target });
      }
    }

    // Per-type counts for the UI breakdown
    const cgSourceCount = sourceAssetDetails.filter(d => d.target === 'CG').length;
    const ribbonSourceCount = sourceAssetDetails.filter(d => d.target === 'Ribbon').length;
    const cgMissing = missingItems.filter(d => d.where === 'CG').length;
    const ribbonMissing = missingItems.filter(d => d.where === 'Ribbon').length;

    console.log(`[checkSyncStatus] ${sourceAssetCount} source assets (${sourceRecordCount} records), ${missing} missing from operator views`);

    return {
      sourceAssetCount,
      sourceRecordCount,
      sourceCount: sourceRecordCount, // backward compat
      operatorCount: existingCgRows.length + existingRibbonRows.length,
      cgAssetCount: cgSourceCount,
      cgOperatorCount: existingCgRows.length,
      cgMissing,
      ribbonAssetCount: ribbonSourceCount,
      ribbonOperatorCount: existingRibbonRows.length,
      ribbonMissing,
      missing,
      missingItems,
      synced: missing === 0,
      status: missing === 0 ? 'synced' : 'out_of_sync',
    };
  } catch (err) {
    console.warn('[checkSyncStatus] Failed:', err.message);
    return { sourceAssetCount: 0, sourceRecordCount: 0, sourceCount: 0, operatorCount: 0, cgAssetCount: 0, cgOperatorCount: 0, cgMissing: 0, ribbonAssetCount: 0, ribbonOperatorCount: 0, ribbonMissing: 0, missing: 0, synced: true, status: 'error', error: err.message };
  }
}


const bidirectionalSync = {
  syncSourceToOperatorViews,
  syncOperatorStatusToSource,
  computeAssetFingerprint,
  isBidirectionalSyncConfigured,
  getSourceTableIdForMode,
  shouldSyncToOperator,
  forceResyncAllToOperator,
  checkSyncStatus,
  checkSourceRecordsExist,
};

/**
 * Batch-check whether source record IDs still exist in the MatchFlow Builder tables.
 * Returns a Set of source record IDs that NO LONGER exist (stale/deleted).
 *
 * Queries both Game Timeline and Team List tables.
 * Uses RECORD_ID() formula for efficient batch lookup (up to 50 per query).
 *
 * @param {string[]} sourceRecordIds — Array of Airtable record IDs to check
 * @returns {Promise<Set<string>>} — Set of IDs that are stale (no longer in builder)
 */
export async function checkSourceRecordsExist(sourceRecordIds) {
  if (!sourceRecordIds || sourceRecordIds.length === 0) return new Set();

  const uniqueIds = [...new Set(sourceRecordIds)];
  const existingIds = new Set();

  // Check both source tables
  const tablesToCheck = [
    GAME_TIMELINE_TABLE_ID,
    TEAM_LIST_TABLE_ID,
  ].filter(Boolean);

  // Batch in groups of 50 to keep formula length manageable
  const BATCH_SIZE = 50;
  for (const tableId of tablesToCheck) {
    for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
      const batch = uniqueIds.slice(i, i + BATCH_SIZE);
      // Only check IDs we haven't already confirmed exist
      const toCheck = batch.filter(id => !existingIds.has(id));
      if (toCheck.length === 0) continue;

      const orClauses = toCheck.map(id => `RECORD_ID()='${id}'`).join(',');
      const formula = `OR(${orClauses})`;
      const params = new URLSearchParams();
      params.set('filterByFormula', formula);
      params.set('fields[]', 'Name'); // Minimal fields to reduce payload

      try {
        const url = `${tableUrl(BASE_ID, tableId)}?${params.toString()}`;
        const data = await airtableFetch(url);
        for (const record of (data.records || [])) {
          existingIds.add(record.id);
        }
      } catch (err) {
        console.warn('[bidirectionalSync] checkSourceRecordsExist failed for table', tableId, err);
      }
    }
  }

  // Stale = in the input list but NOT found in any source table
  const staleIds = new Set();
  for (const id of uniqueIds) {
    if (!existingIds.has(id)) staleIds.add(id);
  }

  if (staleIds.size > 0) {
    console.log(`[bidirectionalSync] Found ${staleIds.size} stale source record(s) out of ${uniqueIds.length} checked`);
  }

  return staleIds;
}

export default bidirectionalSync;
